import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

// Halaman "Catchup Class" di portal student: daftar rekaman sesi yang tidak
// dihadiri, dan pemutar videonya. Menonton sampai ambang yang ditentukan
// server mengubah absensi sesi itu menjadi hadir.
//
// Semua aturan (boleh ditonton atau tidak, berapa lama sudah ditonton, kapan
// dihitung hadir) diputuskan server — lihat MakeupClassController.js. Yang ada
// di sini hanya pemutar yang tidak menyediakan cara untuk melompat, ditambah
// laporan posisi berkala (heartbeat).

const toDate = (iso) => new Date(`${iso}T00:00:00Z`);
const longDate = (iso) =>
  new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", day: "numeric", month: "long", year: "numeric" }).format(toDate(iso));

const pad = (n) => String(n).padStart(2, "0");

const clock = (seconds) => {
  const s = Math.max(0, Math.floor(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}:${pad(m)}:${pad(s % 60)}` : `${m}:${pad(s % 60)}`;
};

export const lengthText = (seconds) => {
  const minutes = Math.max(1, Math.round(seconds / 60));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
};

// Selisih posisi yang masih dianggap pemutaran biasa. Lebih dari ini berarti
// posisinya digeser (lewat tombol media keyboard, devtools, dsb.) dan
// dikembalikan.
const SEEK_TOLERANCE_SECONDS = 3;

// Watermark terus bergerak ke seluruh layar supaya tidak bisa ditutup dengan
// satu kotak tetap di hasil rekaman layar. Layar dibagi 3×3 area; setiap
// putaran mengunjungi kesembilan area (urutan acak, posisi acak di dalam
// area), jadi semua sudut — termasuk kanan bawah — pasti didatangi.
const WATERMARK_MOVE_MS = 7000;
const ZONES = [0, 1, 2].flatMap((row) => [0, 1, 2].map((col) => ({ row, col })));

const shuffledZones = (avoid) => {
  const zones = [...ZONES];
  for (let i = zones.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [zones[i], zones[j]] = [zones[j], zones[i]];
  }
  // Jangan mulai putaran baru di area tempat watermark sedang berada.
  if (avoid && zones[0].row === avoid.row && zones[0].col === avoid.col) zones.push(zones.shift());
  return zones;
};

// x dan y 0–100: 0 = menempel kiri/atas, 100 = menempel kanan/bawah. Lihat
// watermarkStyle — watermark selalu utuh di dalam bingkai.
const pointIn = ({ row, col }) => ({
  x: Math.round(col * 33.3 + Math.random() * 33.3),
  y: Math.round(row * 33.3 + Math.random() * 33.3),
});

// left/top X% ditambah translate(-X%) membuat X = 100 menempelkan sisi kanan
// watermark ke sisi kanan bingkai, berapa pun panjang teksnya.
const watermarkStyle = ({ x, y }) => ({
  left: `${x}%`,
  top: `${y}%`,
  transform: `translate(-${x}%, -${y}%)`,
});

const absoluteUrl = (url) => `${axios.defaults.baseURL || ""}${url}`;

// hls.js (versi light: tanpa subtitle/DRM yang tidak dipakai) baru diunduh
// saat pemutar dibuka, supaya halaman portal lain tidak ikut berat.
const loadHls = () => import("hls.js/light").then((module) => module.default);

const LOAD_FAILED = "Unable to load the video. Please go back and try again.";

// Videonya dikirim sebagai potongan terenkripsi (HLS) dan diputar lewat URL
// blob:, jadi tidak ada file video yang bisa disimpan. Server hanya memberikan
// potongan di sekitar posisi tonton (lihat backend/utils/videoCache.js).
//
// Dipakai juga oleh Overseas Class (OverseasClass.js) dengan `apiBase` dan
// catatan ketersediaan miliknya sendiri — aturan pemutarnya sama.
export const CatchupPlayer = ({
  item,
  thresholdPercent,
  onExit,
  onCompleted,
  apiBase = "/studentportal/makeup",
  availabilityNote,
}) => {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  // Pesan yang menghentikan pemutaran untuk seterusnya (dibuka di tempat
  // lain, batas tanggal lewat, session habis).
  const [blocked, setBlocked] = useState("");
  const [playing, setPlaying] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(item.durationSeconds);
  const [percent, setPercent] = useState(item.percent);
  const [completed, setCompleted] = useState(item.completed);
  const [muted, setMuted] = useState(false);
  const [spot, setSpot] = useState(() => pointIn(ZONES[0]));
  // Catchup Class: rekaman sesi yang dihadiri hanya ditonton ulang, absensinya
  // tidak berubah. Overseas Class tidak mengirim `attendance`.
  const fixesAttendance = item.attendance === undefined || item.attendance === "absent" || item.attendance === "makeup";

  const videoRef = useRef(null);
  const frameRef = useRef(null);
  const sessionRef = useRef(null);
  // Detik video di awal stream. Waktu di elemen <video> dihitung dari 0 di
  // titik ini, jadi posisi sebenarnya = offset + currentTime.
  const offsetRef = useRef(0);
  // Posisi terakhir (waktu <video>) yang dicapai lewat pemutaran biasa.
  const allowedRef = useRef(0);
  const stoppedRef = useRef(false);
  // Instance hls.js; null kalau memakai pemutar HLS bawaan (Safari lama).
  const hlsRef = useRef(null);
  // Lewat ref supaya sendPosition tidak dibuat ulang (dan interval heartbeat
  // tidak di-reset) setiap kali komponen induk me-render ulang.
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  const stopWith = useCallback((message) => {
    stoppedRef.current = true;
    if (videoRef.current) videoRef.current.pause();
    setBlocked(message);
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios
      .post(`${apiBase}/${item.videoId}/start`)
      .then((res) => {
        if (cancelled) return;
        sessionRef.current = res.data;
        offsetRef.current = res.data.streamOffset;
        allowedRef.current = Math.max(0, res.data.startAt - res.data.streamOffset);
        setCurrent(res.data.startAt);
        setSession(res.data);
        if (res.data.durationSeconds) setDuration(res.data.durationSeconds);
        setPercent(res.data.percent);
        setCompleted(res.data.completed);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 401) navigate("/portal/login", { replace: true });
        else setError(err.response?.data?.msg || "Unable to open this catchup class. Please try again later.");
      });
    return () => {
      cancelled = true;
    };
  }, [item.videoId, navigate, apiBase]);

  // Memasang stream ke <video>: hls.js di browser yang mendukungnya (Chrome,
  // Edge, Firefox, Safari, iPhone iOS 17.1+), pemutar HLS bawaan di iPhone lama.
  useEffect(() => {
    const v = videoRef.current;
    if (!session || !v) return undefined;
    const url = absoluteUrl(session.streamUrl);
    const nativeHls = Boolean(v.canPlayType("application/vnd.apple.mpegurl"));
    let cancelled = false;
    let mediaRecovered = false;

    loadHls()
      .then((Hls) => {
        if (cancelled) return;
        if (!Hls.isSupported()) {
          if (nativeHls) v.src = url;
          else stopWith("This browser cannot play the video. Please use a recent version of Chrome, Edge, Firefox or Safari.");
          return;
        }
        const hls = new Hls({
          startPosition: allowedRef.current,
          // Server hanya memberikan potongan sampai 2 menit di depan posisi
          // tonton; jangan menyimpan lebih jauh dari itu.
          maxBufferLength: 30,
          maxMaxBufferLength: 45,
          xhrSetup: (xhr) => {
            xhr.withCredentials = true;
          },
        });
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (!data.fatal) return;
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !mediaRecovered) {
            mediaRecovered = true;
            hls.recoverMediaError();
            return;
          }
          stopWith(LOAD_FAILED);
        });
        hls.loadSource(url);
        hls.attachMedia(v);
        hlsRef.current = hls;
      })
      .catch(() => {
        if (cancelled) return;
        if (nativeHls) v.src = url;
        else stopWith("Unable to load the video player. Please check your connection and try again.");
      });

    return () => {
      cancelled = true;
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      } else {
        v.removeAttribute("src");
        v.load();
      }
    };
  }, [session, stopWith]);

  const sendPosition = useCallback(
    async (position) => {
      const active = sessionRef.current;
      if (!active || stoppedRef.current) return;
      try {
        const res = await axios.post(`${apiBase}/${item.videoId}/heartbeat`, {
          playToken: active.playToken,
          position,
        });
        setPercent(res.data.percent);
        if (typeof res.data.resetTo === "number" && videoRef.current) {
          const local = Math.max(0, res.data.resetTo - offsetRef.current);
          allowedRef.current = local;
          videoRef.current.currentTime = local;
        }
        if (res.data.justCompleted) {
          setCompleted(true);
          onCompletedRef.current();
        }
      } catch (err) {
        // Gangguan jaringan diabaikan — heartbeat berikutnya mencoba lagi.
        // Penolakan dari server berarti tontonan ini tidak boleh dilanjutkan.
        if (!err.response || err.response.status >= 500) return;
        stopWith(
          err.response.status === 401
            ? "Your session has expired. Please log in again to continue watching."
            : err.response.data?.msg || "This video can no longer be played.",
        );
      }
    },
    [item.videoId, apiBase, stopWith],
  );

  const beat = useCallback(() => sendPosition(offsetRef.current + allowedRef.current), [sendPosition]);

  useEffect(() => {
    if (!playing || !session) return undefined;
    const id = setInterval(beat, session.heartbeatSeconds * 1000);
    return () => clearInterval(id);
  }, [playing, session, beat]);

  // Meninggalkan halaman: posisi terakhir tetap dilaporkan supaya bagian yang
  // baru saja ditonton tidak hilang.
  useEffect(() => () => sendPosition(offsetRef.current + allowedRef.current), [sendPosition]);

  // Tab yang tidak terlihat tidak sedang ditonton.
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden && videoRef.current) videoRef.current.pause();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  useEffect(() => {
    let queue = shuffledZones(ZONES[0]);
    let zone = ZONES[0];
    const move = () => {
      if (queue.length === 0) queue = shuffledZones(zone);
      zone = queue.shift();
      setSpot(pointIn(zone));
    };
    // Langsung mulai bergerak, tidak menunggu interval pertama.
    const first = setTimeout(move, 50);
    const id = setInterval(move, WATERMARK_MOVE_MS);
    return () => {
      clearTimeout(first);
      clearInterval(id);
    };
  }, []);

  const toggle = () => {
    const v = videoRef.current;
    if (!v || blocked) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  };

  const toggleFullscreen = () => {
    // Yang dibuat layar penuh adalah bingkainya, bukan <video>: layar penuh
    // bawaan video menampilkan kontrol browser (termasuk seek bar) dan
    // menyembunyikan watermark.
    if (document.fullscreenElement) document.exitFullscreen();
    else if (frameRef.current?.requestFullscreen) frameRef.current.requestFullscreen().catch(() => {});
  };

  // Pemutar bawaan Safari tidak punya startPosition seperti hls.js.
  const onLoadedMetadata = (e) => {
    if (!hlsRef.current && allowedRef.current > 0) e.currentTarget.currentTime = allowedRef.current;
  };

  const onTimeUpdate = (e) => {
    const v = e.currentTarget;
    if (v.seeking) return;
    if (Math.abs(v.currentTime - allowedRef.current) > SEEK_TOLERANCE_SECONDS) {
      v.currentTime = allowedRef.current;
      return;
    }
    allowedRef.current = v.currentTime;
    setCurrent(offsetRef.current + v.currentTime);
  };

  const onSeeking = (e) => {
    const v = e.currentTarget;
    if (Math.abs(v.currentTime - allowedRef.current) > SEEK_TOLERANCE_SECONDS) v.currentTime = allowedRef.current;
  };

  const heading = (
    <p className="sp-card-title">
      <span>
        {item.className}
        <span className="sp-cu-sub">
          Day {item.dayNumber} · Session {item.sessionNumber} · {longDate(item.sessionDate)}
        </span>
      </span>
      <button type="button" className="sp-link" onClick={onExit}>
        Back to list
      </button>
    </p>
  );

  if (error) {
    return (
      <div className="sp-card">
        {heading}
        <p className="sp-panel-empty">{error}</p>
      </div>
    );
  }

  return (
    <div className="sp-card">
      {heading}

      {!session ? (
        <p className="sp-panel-empty">Preparing the video...</p>
      ) : (
        <>
          <div className="sp-cu-frame" ref={frameRef} onContextMenu={(e) => e.preventDefault()}>
            <video
              ref={videoRef}
              preload="metadata"
              playsInline
              muted={muted}
              draggable={false}
              controlsList="nodownload noplaybackrate noremoteplayback"
              disablePictureInPicture
              disableRemotePlayback
              onClick={toggle}
              onLoadedMetadata={onLoadedMetadata}
              onTimeUpdate={onTimeUpdate}
              onSeeking={onSeeking}
              onRateChange={(e) => {
                if (e.currentTarget.playbackRate !== 1) e.currentTarget.playbackRate = 1;
              }}
              onPlay={() => {
                setPlaying(true);
                beat();
              }}
              onPause={() => {
                setPlaying(false);
                beat();
              }}
              onEnded={() => {
                setPlaying(false);
                beat();
              }}
              onWaiting={() => setBuffering(true)}
              onPlaying={() => setBuffering(false)}
              onCanPlay={() => setBuffering(false)}
              onError={() => {
                // Hanya untuk pemutar HLS bawaan Safari; kesalahan hls.js
                // ditangani lewat Hls.Events.ERROR.
                if (!hlsRef.current && !stoppedRef.current && videoRef.current?.getAttribute("src")) stopWith(LOAD_FAILED);
              }}
            />

            <span className="sp-cu-watermark" style={watermarkStyle(spot)} aria-hidden="true">
              {(session.watermark || []).map((line) => (
                <span key={line}>{line}</span>
              ))}
            </span>

            {blocked ? (
              <div className="sp-cu-blocked">{blocked}</div>
            ) : (
              <>
                {!playing && (
                  <button type="button" className="sp-cu-bigplay" onClick={toggle} aria-label="Play">
                    <i className="bi bi-play-fill" aria-hidden="true"></i>
                  </button>
                )}
                {playing && buffering && <span className="sp-cu-buffering">Loading...</span>}
              </>
            )}

            <div className="sp-cu-controls">
              <button type="button" onClick={toggle} disabled={Boolean(blocked)} aria-label={playing ? "Pause" : "Play"}>
                <i className={`bi ${playing ? "bi-pause-fill" : "bi-play-fill"}`} aria-hidden="true"></i>
              </button>
              {/* Hanya penunjuk posisi — sengaja bukan slider, tidak bisa diklik. */}
              <div className="sp-cu-track" role="img" aria-label={`${clock(current)} of ${clock(duration)}`}>
                <span style={{ width: `${duration ? Math.min(100, (current / duration) * 100) : 0}%` }}></span>
              </div>
              <span className="sp-cu-time">
                {clock(current)} / {clock(duration)}
              </span>
              <button type="button" onClick={() => setMuted((m) => !m)} aria-label={muted ? "Unmute" : "Mute"}>
                <i className={`bi ${muted ? "bi-volume-mute-fill" : "bi-volume-up-fill"}`} aria-hidden="true"></i>
              </button>
              <button type="button" onClick={toggleFullscreen} aria-label="Full screen">
                <i className="bi bi-arrows-fullscreen" aria-hidden="true"></i>
              </button>
            </div>
          </div>

          <div className="sp-cu-progress">
            <p>
              {completed && fixesAttendance ? (
                <>
                  <span className="sp-badge is-passed">Attendance updated</span> You have watched enough of this class —
                  the session is now marked as attended.
                </>
              ) : completed ? (
                <>
                  <span className="sp-badge is-passed">Watched</span> You have watched enough of this recording.
                </>
              ) : !fixesAttendance ? (
                <>
                  <strong>{percent}%</strong> watched.
                </>
              ) : (
                <>
                  <strong>{percent}%</strong> watched · {thresholdPercent}% is required for this session to be marked as
                  attended.
                </>
              )}
            </p>
            <div className="sp-bar sp-cu-bar" role="img" aria-label={`${percent}% watched`}>
              <span style={{ width: `${percent}%` }}></span>
              <i style={{ left: `${thresholdPercent}%` }} aria-hidden="true"></i>
            </div>
          </div>

          <ul className="sp-cu-notes">
            <li>The video cannot be skipped forward or rewound. You can pause and continue later from where you stopped.</li>
            <li>The video pauses when you leave this tab, and only time actually played is counted.</li>
            <li>{availabilityNote || `This catchup class is available until ${longDate(item.availableUntil)}.`}</li>
          </ul>
        </>
      )}
    </div>
  );
};

const CatchupAction = ({ item, onWatch }) => {
  if (item.canWatch) {
    return (
      <button type="button" className="sp-cu-watch" onClick={() => onWatch(item.videoId)}>
        <i className="bi bi-play-fill" aria-hidden="true"></i>
        {item.lastPosition > 0 ? "Continue" : "Watch"}
      </button>
    );
  }
  if (item.completed) {
    return item.attendance === "attended" ? (
      <span className="sp-badge is-passed">Watched</span>
    ) : (
      <span className="sp-badge is-passed">Attendance updated</span>
    );
  }
  if (item.expired) return <span className="sp-badge is-muted">No longer available</span>;
  return <span className="sp-dim">—</span>;
};

// Status sesi yang TIDAK bisa diminta, untuk daftar di popup permintaan.
const LOCKED_STATUS = {
  approved: { label: "Approved", className: "is-upcoming", icon: "bi-check-circle" },
  pending: { label: "Awaiting admin approval", className: "is-ongoing", icon: "bi-hourglass-split" },
  rejected: { label: "Declined", className: "is-failed", icon: "bi-x-circle" },
  expired: { label: "No longer available", className: "is-muted", icon: "bi-clock-history" },
  makeup: { label: "Attended via catchup", className: "is-passed", icon: "bi-check-circle" },
  noVideo: { label: "Video not available", className: "is-muted", icon: "bi-camera-video-off" },
  upcoming: { label: "Not held yet", className: "is-muted", icon: "bi-calendar-event" },
};

const ATTENDANCE_BADGE = {
  absent: { label: "Absent", className: "is-failed" },
  attended: { label: "Attended", className: "is-passed" },
  makeup: { label: "Attended via catchup", className: "is-passed" },
};

// Satu baris per sesi kelas itu, urut Day lalu Sesi. `item` = data video
// susulan sesi itu dari server (kalau ada); hanya yang item.canRequest yang
// bisa dicentang, sisanya diberi status.
const buildRows = (sessions, items, today) =>
  [...sessions]
    .sort((a, b) => a.dayNumber - b.dayNumber || a.sessionNumber - b.sessionNumber)
    .map((s) => {
      const item = items.find((i) => i.dayNumber === s.dayNumber && i.sessionNumber === s.sessionNumber);
      let locked = null;
      if (!item) locked = !s.status && !(s.date < today) ? "upcoming" : "noVideo";
      else if (item.accessRequest) locked = item.accessRequest;
      else if (!item.canRequest) locked = item.expired ? "expired" : "makeup";
      return { key: `${s.dayNumber}|${s.sessionNumber}`, dayNumber: s.dayNumber, sessionNumber: s.sessionNumber, date: s.date, item, locked };
    });

// Popup permintaan kelas susulan untuk SATU kelas, dibuka dari panel Catchup
// Class pada detail kelas (My Classes). Menampilkan SEMUA sesi kelas itu urut
// Day dan Sesi: sesi yang bisa diminta diberi checkbox (boleh beberapa
// sekaligus, satu alasan), sisanya diberi status (Approved, Awaiting admin
// approval, Video not available, ...). Yang boleh diminta ditentukan server
// (item.canRequest).
//
// `sessions` = attendance.sessions kelas itu (dari overview). `renderGrid(pick)`
// (opsional) menggambar tabel kehadiran kelas itu di atas daftar; sel sesi
// yang bisa diminta ikut mencentang/melepas sesi itu. Tabelnya milik
// StudentPortal.js, jadi dioper sebagai fungsi.
export const CatchupRequestModal = ({ classCode, batch, className, sessions, onClose, onSent, renderGrid }) => {
  const navigate = useNavigate();
  // { items: video susulan kelas ini, today } dari server.
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState([]);
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    axios
      .get("/studentportal/makeup")
      .then((res) => {
        const items = (res.data.items || []).filter(
          (i) => i.classCode === classCode && String(i.batch) === String(batch),
        );
        setData({ items, today: res.data.today || "" });
        setPicked((prev) => prev.filter((id) => items.some((i) => i.videoId === id && i.canRequest)));
      })
      .catch((err) => {
        if (err.response?.status === 401) navigate("/portal/login", { replace: true });
        else setData({ items: [], today: "" });
      });
  }, [classCode, batch, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // Esc menutup popup; halaman di belakangnya tidak ikut bergulir.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !sending) onClose();
    };
    document.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose, sending]);

  const rows = data ? buildRows(sessions || [], data.items, data.today) : [];
  const selectable = rows.filter((r) => !r.locked);
  const absentIds = selectable.filter((r) => r.item.attendance === "absent").map((r) => r.item.videoId);
  const allAbsentPicked = absentIds.length > 0 && absentIds.every((id) => picked.includes(id));
  const toggle = (id) => setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const reasonOk = reason.trim().length >= 5;
  const canSend = picked.length > 0 && reasonOk && !sending;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSend) return;
    setSending(true);
    try {
      const res = await axios.post("/studentportal/makeup/requests", { videoIds: picked, reason: reason.trim() });
      setSending(false);
      onSent();
      Swal.fire({
        text: `${res.data.msg} The recordings will appear in Catchup Class once they are approved.`,
        icon: "success",
        background: "white",
        color: "black",
        confirmButtonColor: "#e03a12",
      });
    } catch (err) {
      setSending(false);
      if (err.response?.status === 401) return navigate("/portal/login", { replace: true });
      Swal.fire({
        text: err.response?.data?.msg || "Unable to send your request. Please try again later.",
        icon: "error",
        background: "white",
        color: "black",
        confirmButtonColor: "#e03a12",
      });
      load();
    }
  };

  const reasonId = `cu-reason-${classCode}-${batch}`;

  return createPortal(
    <div
      className="sp-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !sending) onClose();
      }}
    >
      <form className="sp-modal" role="dialog" aria-modal="true" aria-labelledby="cu-modal-title" onSubmit={submit}>
        <div className="sp-modal-head">
          <div>
            <h3 id="cu-modal-title">Request a catchup class</h3>
            <p>
              {className} · {classCode} · Batch {batch}
            </p>
          </div>
          <button type="button" className="sp-modal-close" onClick={onClose} disabled={sending} aria-label="Close">
            <i className="bi bi-x-lg" aria-hidden="true"></i>
          </button>
        </div>

        <div className="sp-modal-body">
          {data === null ? (
            <p className="sp-panel-empty">Loading sessions...</p>
          ) : rows.length === 0 ? (
            <p className="sp-panel-empty">Sessions for this class have not been scheduled yet.</p>
          ) : (
            <>
              {renderGrid && (
                <div className="sp-cu-grid">
                  <p className="sp-cu-grid-hint">
                    Your attendance in this class.{selectable.length > 0 ? " Tap a session to select it." : ""}
                  </p>
                  {renderGrid({
                    selectable: new Map(selectable.map((r) => [r.key, r.item.videoId])),
                    picked,
                    onToggle: toggle,
                  })}
                </div>
              )}
              <div className="sp-cu-pick-head">
                <span>
                  {selectable.length > 0
                    ? "Choose the sessions you want to catch up on"
                    : "There is no session you can request at the moment."}
                </span>
                {absentIds.length > 1 && (
                  <button
                    type="button"
                    className="sp-link"
                    onClick={() =>
                      setPicked((prev) =>
                        allAbsentPicked
                          ? prev.filter((id) => !absentIds.includes(id))
                          : [...new Set([...prev, ...absentIds])],
                      )
                    }
                  >
                    {allAbsentPicked ? "Clear missed sessions" : "Select all missed sessions"}
                  </button>
                )}
              </div>
              <ul className="sp-cu-pick">
                {rows.map((r) => {
                  const detail = [longDate(r.date), r.item ? lengthText(r.item.durationSeconds) : null]
                    .filter(Boolean)
                    .join(" · ");
                  const text = (
                    <span className="sp-cu-pick-text">
                      <b>
                        Day {r.dayNumber} · Session {r.sessionNumber}
                      </b>
                      <span>{detail}</span>
                    </span>
                  );
                  if (r.locked) {
                    const status = LOCKED_STATUS[r.locked];
                    return (
                      <li key={r.key}>
                        <div className="sp-cu-pick-row is-locked">
                          <i className={`bi ${status.icon} sp-cu-pick-icon`} aria-hidden="true"></i>
                          {text}
                          <span className={`sp-badge ${status.className}`}>{status.label}</span>
                        </div>
                      </li>
                    );
                  }
                  const on = picked.includes(r.item.videoId);
                  const badge = ATTENDANCE_BADGE[r.item.attendance] || { label: "Not recorded", className: "is-muted" };
                  return (
                    <li key={r.key}>
                      <label className={`sp-cu-pick-row${on ? " is-on" : ""}`}>
                        <input type="checkbox" checked={on} onChange={() => toggle(r.item.videoId)} />
                        {text}
                        <span className={`sp-badge ${badge.className}`}>{badge.label}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>

              {selectable.length > 0 && (
                <>
                  <div className="sp-field sp-cu-form">
                    <label htmlFor={reasonId}>Why could you not attend?</label>
                    <textarea
                      id={reasonId}
                      rows={3}
                      maxLength={500}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="e.g. I was sick, or I had to work that day"
                    />
                  </div>
                  <p className="sp-cu-form-note">
                    Once approved, the recordings appear in Catchup Class and can be watched until their
                    available-until date.
                  </p>
                </>
              )}
            </>
          )}
        </div>

        <div className="sp-modal-foot">
          <button type="button" className="sp-btn is-ghost" onClick={onClose} disabled={sending}>
            {selectable.length > 0 ? "Cancel" : "Close"}
          </button>
          {selectable.length > 0 && (
            <button type="submit" className="sp-btn" disabled={!canSend}>
              {sending
                ? "Sending..."
                : picked.length > 1
                  ? `Send ${picked.length} requests`
                  : "Send request"}
            </button>
          )}
        </div>
      </form>
    </div>,
    // Di dalam .sp-app supaya variabel warna dan font portal tetap berlaku.
    document.querySelector(".sp-app") || document.body,
  );
};

const REQUEST_BADGE = {
  pending: <span className="sp-badge is-ongoing">Waiting for approval</span>,
  rejected: <span className="sp-badge is-failed">Declined</span>,
};

const ClassCell = ({ item }) => (
  <td>
    <span className="sp-class-name">{item.className}</span>
    <span className="sp-class-code">
      {item.classCode} · Batch {item.batch}
    </span>
  </td>
);

// data-label + satu <span> pembungkus: di layar sempit tabel menjadi kartu
// (lihat "Tabel menjadi kartu" di studentPortal.css).
const SessionCell = ({ item }) => (
  <td className="sp-nowrap" data-label="Session">
    <span>
      Day {item.dayNumber} · Session {item.sessionNumber}
      <span className="sp-class-code">{longDate(item.sessionDate)}</span>
    </span>
  </td>
);

const CatchupClass = ({ onAttendanceChanged }) => {
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading", items: [], thresholdPercent: 80 });
  const [activeId, setActiveId] = useState(null);

  const load = useCallback(() => {
    axios
      .get("/studentportal/makeup")
      .then((res) =>
        setState({ status: "ready", items: res.data.items || [], thresholdPercent: res.data.thresholdPercent }),
      )
      .catch((err) => {
        if (err.response?.status === 401) navigate("/portal/login", { replace: true });
        else setState((s) => ({ ...s, status: "error" }));
      });
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const active = state.items.find((i) => i.videoId === activeId);
  if (active) {
    return (
      <CatchupPlayer
        // key: pindah video harus memulai pemutar dari keadaan bersih.
        key={active.videoId}
        item={active}
        thresholdPercent={state.thresholdPercent}
        onCompleted={onAttendanceChanged}
        onExit={() => {
          setActiveId(null);
          load();
        }}
      />
    );
  }

  if (state.status !== "ready") {
    return (
      <div className="sp-card">
        <p className="sp-panel-empty">
          {state.status === "loading"
            ? "Loading catchup classes..."
            : "Unable to load the catchup classes right now. Please try again later."}
        </p>
      </div>
    );
  }

  // Rekaman hanya muncul setelah permintaannya disetujui admin.
  const recordings = state.items.filter((i) => i.accessRequest === "approved");
  const requests = state.items
    .filter((i) => i.accessRequest === "pending" || i.accessRequest === "rejected")
    .sort((a, b) => (a.requestedAt < b.requestedAt ? 1 : -1));

  return (
    <>
      {recordings.length > 0 && (
        <div className="sp-card">
          <p className="sp-card-title">Approved recordings</p>
          <div className="sp-table-wrap">
            <table className="sp-table">
              <thead>
                <tr>
                  <th>Class</th>
                  <th>Session</th>
                  <th>Length</th>
                  <th>Available until</th>
                  <th>Watched</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {recordings.map((item) => (
                  <tr key={item.videoId}>
                    <ClassCell item={item} />
                    <SessionCell item={item} />
                    <td className="sp-nowrap" data-label="Length">
                      <span>{lengthText(item.durationSeconds)}</span>
                    </td>
                    <td className="sp-nowrap" data-label="Available until">
                      <span>{longDate(item.availableUntil)}</span>
                    </td>
                    <td className="sp-cu-watched" data-label="Watched">
                      <span>
                        <span className="sp-bar" role="img" aria-label={`${item.percent}% watched`}>
                          <span style={{ width: `${item.percent}%` }}></span>
                        </span>
                        <span className="sp-class-code">{item.percent}%</span>
                      </span>
                    </td>
                    <td className="sp-td-action sp-nowrap">
                      <CatchupAction item={item} onWatch={setActiveId} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {requests.length > 0 && (
        <div className="sp-card">
          <p className="sp-card-title">My requests</p>
          <div className="sp-table-wrap">
            <table className="sp-table">
              <thead>
                <tr>
                  <th>Class</th>
                  <th>Session</th>
                  <th>Reason</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((item) => (
                  <tr key={item.videoId}>
                    <ClassCell item={item} />
                    <SessionCell item={item} />
                    <td className="sp-cu-reason" data-label="Reason">
                      <span>{item.requestReason || "—"}</span>
                    </td>
                    <td className="sp-nowrap" data-label="Status">
                      <span>{REQUEST_BADGE[item.accessRequest]}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Form permintaannya ada di panel Catchup Class pada detail kelas
          (My Classes), di samping tabel kehadiran kelas itu. */}
      <div className="sp-card">
        <p className="sp-card-title">Request a catchup class</p>
        <p className="sp-cu-intro">
          {recordings.length === 0 && requests.length === 0 ? "You have no catchup class yet. " : ""}
          To request one, open the class in My Classes, choose the session, and tell us why you could not attend. Once
          the academy approves your request, the recording appears on this page. Watching {state.thresholdPercent}% of
          the recording of a session you missed updates your attendance.
        </p>
        <Link className="sp-makeup-btn is-primary sp-cu-goto" to="/portal/classes">
          <i className="bi bi-journal-text" aria-hidden="true"></i>
          Go to My Classes
        </Link>
      </div>
    </>
  );
};

export default CatchupClass;
